import { Vector3 } from 'three';
import { CSS3DSprite } from 'three/examples/jsm/renderers/CSS3DRenderer.js';
import './click-point-animation.css';

const SPARK_COUNT = 8;
const EFFECT_TTL_MS = 1100;
const SURFACE_OFFSET = 12;

/**
 * Click ripple for view-mode hotspots.
 * 2D indicators: DOM child on the indicator.
 * 3D meshes (link / gallery): CSS3DSprite in the room, in front of the plane.
 */
export class ClickPointAnimation {
  constructor({ camera, parent } = {}) {
    this.camera = camera || null;
    this.parent = parent || null;
    this._active = [];
    this._worldPoint = new Vector3();
    this._camWorldPos = new Vector3();
    this._offset = new Vector3();
    this._local = new Vector3();
  }

  playOnElements(elements, { staggerMs = 100, staggerRandom = false } = {}) {
    if (!elements?.length) return;
    elements.forEach((el, i) => {
      const delay = staggerRandom
        ? Math.random() * Math.max(staggerMs, 1)
        : i * staggerMs;
      const timeoutId = window.setTimeout(() => this.playAtElement(el), delay);
      this._active.push({ timeoutId });
    });
  }

  playOnMeshes(meshes, { staggerMs = 100, staggerRandom = false } = {}) {
    if (!meshes?.length) return;
    meshes.forEach((mesh, i) => {
      const delay = staggerRandom
        ? Math.random() * Math.max(staggerMs, 1)
        : i * staggerMs;
      const timeoutId = window.setTimeout(() => this.playAtMesh(mesh), delay);
      this._active.push({ timeoutId });
    });
  }

  playAtElement(el) {
    if (!el?.appendChild) return;
    if (!el.classList.contains('visible')) return;
    const rect = el.getBoundingClientRect();
    if (rect.width < 1 || rect.height < 1) return;

    const effect = this._createEffect('click-point-effect click-point-effect--on-indicator');
    el.appendChild(effect);
    const timeoutId = window.setTimeout(() => effect.remove(), EFFECT_TTL_MS);
    this._active.push({ timeoutId, element: effect });
  }

  playAtMesh(mesh, surfaceOffset = SURFACE_OFFSET) {
    if (!mesh || !this.camera || !this.parent) return;
    mesh.getWorldPosition(this._worldPoint);
    this.camera.getWorldPosition(this._camWorldPos);
    this._offset.copy(this._camWorldPos).sub(this._worldPoint);
    const dist = this._offset.length();
    if (dist > 1e-4) {
      this._offset.multiplyScalar(surfaceOffset / dist);
      this._worldPoint.add(this._offset);
    }
    this.playAt(this._worldPoint);
  }

  playAt(worldPoint) {
    if (!worldPoint || !this.camera || !this.parent) return;

    const effect = this._createEffect('click-point-effect');
    const sprite = new CSS3DSprite(effect);
    this.parent.worldToLocal(this._local.copy(worldPoint));
    sprite.position.copy(this._local);

    this.camera.getWorldPosition(this._camWorldPos);
    const dist = this._camWorldPos.distanceTo(worldPoint);
    const fov = (this.camera.fov * Math.PI) / 180;
    const height = this.camera.parent
      ? (document.querySelector('#webgl')?.clientHeight || window.innerHeight)
      : window.innerHeight;
    const scale = (dist * Math.tan(fov / 2) * 2) / Math.max(height, 1);
    sprite.scale.set(scale, scale, scale);

    this.parent.add(sprite);
    const timeoutId = window.setTimeout(() => {
      this.parent?.remove(sprite);
      effect.remove();
    }, EFFECT_TTL_MS);
    this._active.push({ timeoutId, sprite, element: effect });
  }

  _createEffect(className) {
    const effect = document.createElement('div');
    effect.className = className;
    effect.setAttribute('aria-hidden', 'true');

    const ring = document.createElement('div');
    ring.className = 'click-point-ring';
    effect.appendChild(ring);

    for (let i = 0; i < SPARK_COUNT; i++) {
      const spark = document.createElement('div');
      spark.className = 'click-point-spark';
      spark.style.setProperty('--angle', `${(360 / SPARK_COUNT) * i}deg`);
      spark.style.animationDelay = `${Math.random() * 0.08}s`;
      effect.appendChild(spark);
    }
    return effect;
  }

  destroy() {
    this._active.forEach(({ timeoutId, element, sprite }) => {
      window.clearTimeout(timeoutId);
      if (sprite && this.parent) this.parent.remove(sprite);
      element?.remove();
    });
    this._active = [];
    this.camera = null;
    this.parent = null;
  }
}
